import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FileDownloadOutlined } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import FormHelperText from "@mui/material/FormHelperText";
import IconButton from "@mui/material/IconButton";
import Radio from "@mui/material/Radio";
import { useTheme } from "@mui/material/styles";
import Accordion from "@components/Accordion/Accordion";
import Autocomplete from "@components/Autocomplete/Autocomplete";
import type { AutocompleteOption } from "@components/Autocomplete/Autocomplete.types";
import Button from "@components/Button/Button.tsx";
import DatePicker from "@components/DatePicker/DatePicker";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants.ts";
import { DocumentUpload } from "@components/FileUpload/FileUpload.tsx";
import SingleFile from "@components/FileUpload/SingleFile.tsx";
import { useUploadStore } from "@components/FileUpload/uploadStore";
import type { UploadEntry } from "@components/FileUpload/uploadStore";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import Input from "@components/Input/Input";
import Select from "@components/Select/Select";
import {
  DRAWER_TEXT_MAX_CHARS,
  getDrawerSchema,
  normalizeDrawerText,
} from "@components/SideDrawer/SideDrawer.schema.ts";
import { getSideDrawerStyles } from "@components/SideDrawer/SideDrawer.styles";
import type {
  SideDrawerAttachmentRef,
  SideDrawerProps,
} from "@components/SideDrawer/SideDrawer.types";
import { getFileTitle } from "@features/port-security/components/utils";
import { validityPeriodStringToInt } from "@features/port-security/components/utils.ts";
import {
  getCrewFormSchema,
  type CrewFormSchema,
} from "@features/vessel-call/create/schema/CrewForm.schema";
import {
  getPassengerFormSchema,
  type PassengerFormValues,
} from "@features/vessel-call/create/schema/PassengerForm.schema";
import type { PortCallDateEntry } from "@features/vessel-call/create/steps/PreArrivalNotification/portCallDateValidation";
import {
  getPortCallFormSchema,
  normalizeToStartOfDay,
  type PortCallsSchema,
} from "@features/vessel-call/create/steps/PreArrivalNotification/PortCalls.schema";
import {
  buildPortOptions,
  type PortOption,
} from "@features/vessel-call/create/utils/portOptions.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import type { CrewRank, Nationality } from "@services/vessel-call/types";
import type { Country, Port } from "@services/vessel/types";
import { Controller, useForm, useWatch, type Resolver, type UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

function resolveSideDrawerAttachment(ref: SideDrawerAttachmentRef): {
  url: string;
  displayName: string;
} {
  if (typeof ref === "string") {
    return { url: ref, displayName: getFileTitle(ref, undefined) };
  }
  const url = ref.url ?? "";
  const fromName =
    typeof ref.originalFileName === "string" && ref.originalFileName.trim()
      ? ref.originalFileName.trim()
      : "";
  return { url, displayName: fromName || getFileTitle(url, undefined) };
}

type PortCallPropsAddition = {
  portCallInitialValues?: Partial<PortCallsSchema>;
  onPersistDraft?: (draft: Partial<PortCallsSchema> | null) => void;
  portCallExistingEntries?: PortCallDateEntry[];
  portCallCurrentId?: string;
  portCallEnforceUniqueDates?: boolean;
  portCallPortsOptions?: (countryName?: string) => AutocompleteOption[];
  portCallCountriesOptions?: AutocompleteOption[];
  onPortCallPortChange?: (
    opt: AutocompleteOption | null,
    form: UseFormReturn<PortCallsSchema>,
  ) => void;
  onPortCallCountryChange?: (
    opt: AutocompleteOption | null,
    form: UseFormReturn<PortCallsSchema>,
  ) => void;
};

type CrewPropsAddition = {
  crewInitialValues?: Partial<CrewFormSchema>;
  onPersistDraft?: (draft: Partial<CrewFormSchema> | null) => void;
  validateCrewSubmission?: (
    values: CrewFormSchema,
  ) => Partial<Record<"rank" | "passportNumber", string>> | null;
};

type PassengerPropsAddition = {
  passengerInitialValues?: Partial<PassengerFormValues>;
  onPersistDraft?: (draft: Partial<PassengerFormValues> | null) => void;
  validatePassengerSubmission?: (
    values: PassengerFormValues,
  ) => Partial<Record<"identityDocumentNumber", string>> | null;
};

const LANG_BASE = "security-clearance";
const TEST_ID_BASE = "side-drawer";
const EMPTY_UPLOAD_ENTRIES: UploadEntry[] = [];
const TEXTAREA_MAX_CHARS = DRAWER_TEXT_MAX_CHARS;

const SideDrawer: React.FC<
  SideDrawerProps & PortCallPropsAddition & CrewPropsAddition & PassengerPropsAddition
> = ({
  isOpen,
  onClose,
  type,
  title,
  description: drawerDescription,
  notesLabel,
  submitButtonLabel,
  onSubmit,
  requestId,
  fileUploadTitle,
  additionalFileUploadTitle,
  onDocumentUpload,
  onAdditionalDocumentUpload,
  staticContent,
  dropdownLabel,
  dropdownOptions,
  dropdownCurrentOption,
  externalNotes,
  internalNotes,
  cancelAttachment,
  externalNotesDocument,
  internalNotesDocument,
  externalNotesDocumentName,
  internalNotesDocumentName,
  internalNotesLabel,
  externalNotesLabel,
  internalAttachmentUploadTitle,
  externalAttachmentUploadTitle,
  detailsAttachments,
  detailsHistory,
  onAttachmentDownload,
  onDownload,
  onAdditionalDownload,
  onPreview,
  onAdditionalPreview,
  onRemoveUploadedAttachmentUrl,
  isExternalDocumentUploadPending = false,
  isInternalDocumentUploadPending = false,
  shouldShowLoadingFileToast = false,
  isSubmitting = false,
  portCallInitialValues,
  portCallExistingEntries,
  portCallCurrentId,
  portCallEnforceUniqueDates = false,
  portCallPortsOptions,
  portCallCountriesOptions,
  onPortCallPortChange,
  onPortCallCountryChange,
  crewInitialValues,
  passengerInitialValues,
  onPersistDraft,
  validateCrewSubmission,
  validatePassengerSubmission,
  showChangeValidityPeriod = false,
}) => {
  const FILE_UPLOAD_PROFILES = useFileUploadProfiles();
  const theme = useTheme();
  const { t } = useTranslation();
  const styles = getSideDrawerStyles(theme);
  const [description, setDescription] = useState("");
  const [mandatoryDescription, setMandatoryDescription] = useState("");
  const [additionalDescription, setAdditionalDescription] = useState("");
  const setFilesForKey = useUploadStore((state) => state.setFilesForKey);
  const clearFilesForKey = useUploadStore((state) => state.clearFilesForKey);
  const sideDrawerApprovalFile = useUploadStore(
    (state) => state.filesByKey.sideDrawerApprovalFile ?? EMPTY_UPLOAD_ENTRIES,
  );
  const sideDrawerAdditionalFile = useUploadStore(
    (state) => state.filesByKey.sideDrawerAdditionalFile ?? EMPTY_UPLOAD_ENTRIES,
  );

  const asDateOrNull = (v: unknown) => (v == null || v === "" ? null : (v as Date));
  const today = normalizeToStartOfDay(new Date());

  const setSideDrawerApprovalFile = useCallback(
    (files: UploadEntry[]) => {
      if (!files.length) {
        clearFilesForKey("sideDrawerApprovalFile");
        return;
      }
      setFilesForKey("sideDrawerApprovalFile", files);
    },
    [clearFilesForKey, setFilesForKey],
  );
  const setSideDrawerAdditionalFile = useCallback(
    (files: UploadEntry[]) => {
      if (!files.length) {
        clearFilesForKey("sideDrawerAdditionalFile");
        return;
      }
      setFilesForKey("sideDrawerAdditionalFile", files);
    },
    [clearFilesForKey, setFilesForKey],
  );
  const [errors, setErrors] = useState<{
    description?: string;
    mandatoryDescription?: string;
    files?: string;
    selectedOption?: string;
  }>({});
  const [additionalErrors, setAdditionalErrors] = useState<{
    description?: string;
    mandatoryDescription?: string;
    additionalDescription?: string;
    files?: string;
  }>({});
  const [selectedOption, setSelectedOption] = useState("");

  const portCallForm = useForm<PortCallsSchema>({
    resolver: zodResolver(getPortCallFormSchema(t)),
    defaultValues: {
      port: "",
      country: "",
      securityLevelOfPortCall: "",
      arrivalDate: "",
      departureDate: "",
      comments: "",
    },
    mode: "onChange",
  });

  const validatePortCallWithContext = useCallback(
    (fields?: Array<keyof PortCallsSchema>) => {
      const result = getPortCallFormSchema(t, {
        existingPortCalls: portCallExistingEntries,
        currentPortCallId: portCallCurrentId,
        enforceUniqueDates: portCallEnforceUniqueDates,
      }).safeParse(portCallForm.getValues());

      const targetFields = fields?.length ? fields : undefined;

      if (targetFields) {
        portCallForm.clearErrors(targetFields);
      } else {
        portCallForm.clearErrors();
      }

      if (result.success) {
        return true;
      }

      result.error.issues.forEach((issue) => {
        const fieldName = issue.path[0];

        if (typeof fieldName !== "string") {
          return;
        }

        if (targetFields && !targetFields.includes(fieldName as keyof PortCallsSchema)) {
          return;
        }

        portCallForm.setError(fieldName as keyof PortCallsSchema, {
          type: "manual",
          message: issue.message,
        });
      });

      return false;
    },
    [portCallCurrentId, portCallEnforceUniqueDates, portCallExistingEntries, portCallForm, t],
  );

  function buildPortCallDefaults(src?: Partial<PortCallsSchema>) {
    return {
      port: src?.port ?? "",
      country: src?.country ?? "",
      securityLevelOfPortCall: src?.securityLevelOfPortCall ?? "",
      arrivalDate: src?.arrivalDate ?? "",
      departureDate: src?.departureDate ?? "",
      comments: src?.comments ?? "",
    } as const;
  }

  const crewForm = useForm<CrewFormSchema>({
    resolver: zodResolver(getCrewFormSchema(t)),
    defaultValues: {
      familyName: "",
      givenName: "",
      rank: "",
      nationality: "",
      placeOfBirth: "",
      dateOfBirth: undefined,
      gender: "",
      passportNumber: "",
      issuingState: "",
      expiryDate: undefined,
    },
    mode: "onChange",
  });

  function buildCrewDefaults(src?: Partial<CrewFormSchema>) {
    return {
      familyName: src?.familyName ?? "",
      givenName: src?.givenName ?? "",
      rank: src?.rank ?? "",
      nationality: src?.nationality ?? "",
      placeOfBirth: src?.placeOfBirth ?? "",
      dateOfBirth: src?.dateOfBirth,
      gender: src?.gender,
      passportNumber: src?.passportNumber ?? "",
      issuingState: src?.issuingState ?? "",
      expiryDate: src?.expiryDate,
    } as const;
  }

  const passengerForm = useForm<PassengerFormValues>({
    resolver: zodResolver(getPassengerFormSchema(t)) as Resolver<PassengerFormValues>,
    defaultValues: {
      familyName: "",
      givenName: "",
      nationality: "",
      dateOfBirth: undefined,
      placeOfBirth: "",
      gender: "",
      identityDocumentNumber: "",
      issuingState: "",
      expiryDate: undefined,
      portOfEmbarkation: "",
      portOfDisembarkation: "",
      transitPassenger: false,
    },
    mode: "onChange",
  });

  function buildPassengerDefaults(src?: Partial<PassengerFormValues>) {
    return {
      familyName: src?.familyName ?? "",
      givenName: src?.givenName ?? "",
      nationality: src?.nationality ?? "",
      dateOfBirth: src?.dateOfBirth ?? undefined,
      placeOfBirth: src?.placeOfBirth ?? "",
      gender: src?.gender ?? "",
      identityDocumentNumber: src?.identityDocumentNumber ?? "",
      issuingState: src?.issuingState ?? "",
      expiryDate: src?.expiryDate ?? undefined,
      portOfEmbarkation: src?.portOfEmbarkation ?? "",
      portOfDisembarkation: src?.portOfDisembarkation ?? "",
      transitPassenger: src?.transitPassenger ?? false,
    } as const;
  }

  const passengerDraft = useWatch({
    control: passengerForm.control,
  });

  const crewDraft = useWatch({
    control: crewForm.control,
  });

  const portCallDraft = useWatch({
    control: portCallForm.control,
  });
  const portCallArrivalDate = asDateOrNull(portCallDraft.arrivalDate);
  const portCallDepartureDate = asDateOrNull(portCallDraft.departureDate);
  const portCallArrivalMaxDate =
    portCallDepartureDate &&
    normalizeToStartOfDay(portCallDepartureDate).getTime() < today.getTime()
      ? portCallDepartureDate
      : today;
  const portCallDepartureMinDate =
    portCallArrivalDate && normalizeToStartOfDay(portCallArrivalDate).getTime() <= today.getTime()
      ? portCallArrivalDate
      : undefined;

  useEffect(() => {
    if (!isOpen) return;
    if (type === "addPortCall" || type === "editPortCall") {
      portCallForm.reset(buildPortCallDefaults(portCallInitialValues));
    }
    if (type === "addCrew" || type === "editCrew") {
      crewForm.reset(buildCrewDefaults(crewInitialValues));
    }
    if (type === "addPassenger" || type === "editPassenger") {
      passengerForm.reset(buildPassengerDefaults(passengerInitialValues));
    }
  }, [isOpen, type, portCallForm, crewForm, passengerForm]);

  const profile = FILE_UPLOAD_PROFILES.sideDrawerApprovalFile;
  const allowedSideDrawerExtensions = profile.format.map((ext) =>
    ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
  );
  const maxSizeBytes = profile.maxSizeBytes;
  const maxSizeLabel = profile.maxSizeLabel;
  const allowedFormats = profile.format;

  const { options: nationalities } = useFormattedReference<Nationality>(
    REFERENCE_DATA_TYPES.NATIONALITY,
    "bff-gatepass",
  );

  const { options: crewRanks } = useFormattedReference(
    REFERENCE_DATA_TYPES.CREW_RANK,
    "bff-vessel-call",
  );

  const { options: countries } = useFormattedReference<Country>(
    REFERENCE_DATA_TYPES.COUNTRIES,
    "bff-vessel",
  );

  const crewRankOptions = crewRanks
    .filter(
      (crewRank): crewRank is CrewRank & { crewRankCode: string; crewRankDesc: string } =>
        !!((crewRank as CrewRank)?.crewRankCode && (crewRank as CrewRank)?.crewRankDesc),
    )
    .map(({ crewRankCode, crewRankDesc }) => ({
      value: crewRankCode,
      label: crewRankDesc,
    }));

  const { options: ports, isLoading: isPortsLoading } = useFormattedReference<Port>(
    REFERENCE_DATA_TYPES.PORTS,
    "bff-vessel",
  );
  const nationalitiesOptions = useMemo(
    () =>
      nationalities
        .map((n) => ({ label: n.nationality, value: n.nationality }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [nationalities],
  );

  const issuingStateCountriesOptions = useMemo<AutocompleteOption[]>(
    () =>
      countries
        .filter(
          (country): country is Country & { countryCode: string; countryName: string } =>
            !!(country?.countryCode && country?.countryName),
        )
        .map(({ countryCode, countryName }) => ({
          value: countryCode,
          label: `${countryName} (${countryCode})`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [countries],
  );

  const portsOptions = buildPortOptions(ports);

  const selectedCountryCode = useWatch({ control: portCallForm.control, name: "country" });
  const countriesOptionsPortCall = portCallCountriesOptions ?? [];
  const portsOptionsPortCall = portCallPortsOptions?.(selectedCountryCode) ?? [];

  const handlePortCallPortChange = useCallback(
    (opt: AutocompleteOption | null) => {
      if (onPortCallPortChange) {
        onPortCallPortChange(opt, portCallForm);
        return;
      }
    },
    [onPortCallPortChange, portCallForm],
  );

  const handlePortCallCountryChange = useCallback(
    (opt: AutocompleteOption | null) => {
      if (onPortCallCountryChange) {
        onPortCallCountryChange(opt, portCallForm);
        return;
      }
    },
    [onPortCallCountryChange, portCallForm],
  );

  const securityLevelOptions: AutocompleteOption[] = [
    { value: 1, label: "1" },
    { value: 2, label: "2" },
    { value: 3, label: "3" },
  ];

  const hasExternalInternal =
    (type === "approval" || type === "reject") &&
    (internalNotesLabel ||
      externalNotesLabel ||
      internalAttachmentUploadTitle ||
      externalAttachmentUploadTitle);

  // Primary attachment slot
  const showsPrimaryDocumentUpload =
    (type === "approval" && !showChangeValidityPeriod) || type === "reject";

  const approvalUploadSignatureRef = useRef<string | null>(null);
  const additionalUploadSignatureRef = useRef<string | null>(null);

  const getFileSignature = (entry: UploadEntry | undefined) => {
    if (!(entry instanceof File)) return null;
    return `${entry.name}-${entry.lastModified}-${entry.size}`;
  };

  const triggerUpload = useCallback(
    (
      entry: UploadEntry | undefined,
      handler: ((file: File) => void) | undefined,
      tracker: React.MutableRefObject<string | null>,
    ) => {
      if (!handler) return;

      if (!(entry instanceof File)) return;

      const signature = getFileSignature(entry);

      if (!signature) return;

      if (tracker.current === signature) return;

      tracker.current = signature;
      handler(entry);
    },
    [],
  );

  const descriptionOnlySchema = getDrawerSchema(t).pick({ description: true });
  const mandatoryDescriptionOnlySchema = getDrawerSchema(t).pick({ mandatoryDescription: true });
  const additionalDescriptionOnlySchema = getDrawerSchema(t).pick({ additionalDescription: true });
  const statusOnlySchema = getDrawerSchema(t).pick({ selectedOption: true, description: true });

  const validityPeriodOnlySchema = getDrawerSchema(t).pick({
    selectedOption: true,
  });

  const inputSlotProps = {
    root: {
      className: `rounded-lg text-sm px-4 py-2 border`,
      style: {
        borderColor: theme.palette.strokeColor?.primary ?? "#BBC1C5",
        color: theme.palette.text.primary,
      },
    },
    htmlInput: {
      maxLength: TEXTAREA_MAX_CHARS,
    },
  };

  const sxProps = {
    "& .MuiInputBase-root": {
      borderRadius: "8px",
      borderColor: theme.palette.strokeColor?.primary ?? "#BBC1C5",
      padding: "12px",
      fontSize: theme.typography.body1.fontSize,
      color: theme.palette.text.primary,
    },
    "& textarea": {
      padding: 0,
    },
  };

  useEffect(() => {
    if (sideDrawerApprovalFile.length === 0) {
      approvalUploadSignatureRef.current = null;
      return;
    }
    const latestEntry = sideDrawerApprovalFile[sideDrawerApprovalFile.length - 1];
    triggerUpload(latestEntry, onDocumentUpload, approvalUploadSignatureRef);
  }, [sideDrawerApprovalFile, onDocumentUpload, triggerUpload]);

  useEffect(() => {
    if (sideDrawerAdditionalFile.length === 0) {
      additionalUploadSignatureRef.current = null;
      return;
    }
    const latestEntry = sideDrawerAdditionalFile[sideDrawerAdditionalFile.length - 1];
    triggerUpload(latestEntry, onAdditionalDocumentUpload, additionalUploadSignatureRef);
  }, [sideDrawerAdditionalFile, onAdditionalDocumentUpload, triggerUpload]);
  const isPrintableKey = (e: React.KeyboardEvent) =>
    e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;

  const charCounterSx = {
    ml: 0,
    mt: 0.5,
    fontSize: 12,
    textAlign: "right",
    color: "text.secondary",
  };
  const isDocumentUploadPending =
    isExternalDocumentUploadPending || isInternalDocumentUploadPending;
  const shouldBlockSubmitForPendingUpload = !shouldShowLoadingFileToast && isDocumentUploadPending;

  const handleSubmit = () => {
    if (isSubmitting) return;

    if (type === "addPortCall" || type === "editPortCall") {
      const portCallValues = portCallForm.getValues();

      if (!validatePortCallWithContext()) {
        return;
      }

      onSubmit?.(portCallValues);
      portCallForm.reset();
      onClose();
      return;
    }
    if (type === "addCrew" || type === "editCrew") {
      const crewValues = crewForm.getValues();
      crewForm.clearErrors(["rank", "passportNumber"]);
      const result = getCrewFormSchema(t).safeParse(crewValues);

      if (!result.success) {
        crewForm.trigger();
        return;
      }

      const customErrors = validateCrewSubmission?.(crewValues);

      if (customErrors) {
        Object.entries(customErrors).forEach(([field, message]) => {
          if (!message) return;
          crewForm.setError(field as "rank" | "passportNumber", {
            type: "manual",
            message,
          });
        });
        return;
      }

      onSubmit?.(crewValues);
      crewForm.reset();
      onClose();
      return;
    }

    if (type === "addPassenger" || type === "editPassenger") {
      const passengerValues = passengerForm.getValues();
      passengerForm.clearErrors(["identityDocumentNumber"]);
      const result = getPassengerFormSchema(t).safeParse(passengerValues);

      if (!result.success) {
        passengerForm.trigger();
        return;
      }

      const customErrors = validatePassengerSubmission?.(passengerValues);

      if (customErrors) {
        Object.entries(customErrors).forEach(([field, message]) => {
          if (!message) return;
          passengerForm.setError(field as "identityDocumentNumber", {
            type: "manual",
            message,
          });
        });
        return;
      }

      onSubmit?.(passengerValues);
      passengerForm.reset();
      onClose();
      return;
    }

    if (type === "status") {
      const normalizedDescription = normalizeDrawerText(description);
      setDescription(normalizedDescription);
      const statusResult = statusOnlySchema.safeParse({
        selectedOption: selectedOption,
        description: normalizedDescription,
      });

      if (!statusResult.success) {
        const flat = statusResult.error.flatten().fieldErrors;
        setErrors({
          selectedOption: flat.selectedOption?.[0],
          description: flat.description?.[0],
        });
      } else {
        setErrors({});
        setDescription("");
        onSubmit?.({ status: selectedOption, description: statusResult.data.description ?? "" });
      }
      return;
    }

    if (type === "approval" && showChangeValidityPeriod) {
      if (shouldBlockSubmitForPendingUpload) return;

      const validityResult = validityPeriodOnlySchema.safeParse({
        selectedOption: selectedOption,
      });

      if (!validityResult.success) {
        const flat = validityResult.error.flatten().fieldErrors;
        setErrors({
          selectedOption: flat.selectedOption?.[0],
        });
      } else {
        setErrors({});
        onSubmit?.({ validityPeriod: validityPeriodStringToInt(selectedOption) });
      }
      return;
    }

    if (shouldBlockSubmitForPendingUpload) return;

    const invalidFile = sideDrawerApprovalFile.find((entry) => {
      if (!(entry instanceof File)) {
        return false;
      }
      const ext = entry.name.split(".").pop()?.toLowerCase();
      const exceedsSize = typeof maxSizeBytes === "number" ? entry.size > maxSizeBytes : false;
      const disallowedFormat = ext ? !allowedFormats.includes(ext) : true;
      return exceedsSize || disallowedFormat;
    });
    if (invalidFile) {
      setErrors({
        files: maxSizeLabel,
        description: errors.description,
        mandatoryDescription: errors.mandatoryDescription,
      });
      return;
    }

    const normalizedDescription = normalizeDrawerText(description);
    const normalizedMandatoryDescription = normalizeDrawerText(mandatoryDescription);
    const normalizedAdditionalDescription = normalizeDrawerText(additionalDescription);
    setDescription(normalizedDescription);
    setMandatoryDescription(normalizedMandatoryDescription);
    setAdditionalDescription(normalizedAdditionalDescription);

    if ((type === "approval" && hasExternalInternal) || type === "reject") {
      const additionalDescResult = additionalDescriptionOnlySchema.safeParse({
        additionalDescription: normalizedAdditionalDescription,
      });
      if (!additionalDescResult.success) {
        setAdditionalErrors((prev) => ({
          ...prev,
          additionalDescription:
            additionalDescResult.error.flatten().fieldErrors.additionalDescription?.[0],
        }));
        return;
      }
      setAdditionalErrors((prev) => ({ ...prev, additionalDescription: undefined }));
    }
    const schemaForSubmit =
      type === "resubmit"
        ? getDrawerSchema(t).pick({
            mandatoryDescription: true,
            description: undefined,
            files: undefined,
          })
        : getDrawerSchema(t).pick({
            description: true,
            files: true,
            mandatoryDescription: undefined,
          });

    const result = schemaForSubmit.safeParse({
      description: normalizedDescription,
      mandatoryDescription: normalizedMandatoryDescription,
      files: sideDrawerApprovalFile,
    });

    if (!result.success) {
      const flat = result.error.flatten().fieldErrors;
      setErrors({
        description: flat.description?.[0],
        mandatoryDescription: flat.mandatoryDescription?.[0],
        files: flat.files?.[0],
      });
    } else {
      setErrors({});
      setAdditionalErrors({});

      if (!shouldShowLoadingFileToast || !isDocumentUploadPending) {
        setDescription("");
        setSideDrawerApprovalFile([]);
        setAdditionalDescription("");
        setSideDrawerAdditionalFile([]);
      }

      onSubmit?.({
        description: normalizedDescription || normalizedMandatoryDescription,
        additionalDescription: normalizedAdditionalDescription,
        internalAttachmentName: sideDrawerAdditionalFile[0]?.name,
        externalAttachmentName: sideDrawerApprovalFile[0]?.name,
      });
    }
  };

  const handleCancelClick = () => {
    if (type === "addPortCall") {
      onPersistDraft?.(null);
    }
    if (type === "addCrew") {
      onPersistDraft?.(null);
    }
    if (type === "addPassenger") {
      onPersistDraft?.(null);
    }
    onClose();
  };

  const handleDrawerClose = (_event: React.MouseEvent, reason: "backdropClick") => {
    if (reason === "backdropClick") {
      if (type === "addPortCall") {
        const draft = portCallForm.getValues() as Partial<PortCallsSchema>;
        onPersistDraft?.(draft);
      }
      if (type === "addCrew") {
        const draft = crewForm.getValues() as Partial<CrewFormSchema>;
        onPersistDraft?.(draft);
      }
      if (type === "addPassenger") {
        const draft = passengerForm.getValues() as Partial<PassengerFormValues>;
        onPersistDraft?.(draft);
      }
    }
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      setDescription("");
      setMandatoryDescription("");
      setAdditionalDescription("");
      setSideDrawerApprovalFile([]);
      setSideDrawerAdditionalFile([]);
      approvalUploadSignatureRef.current = null;
      additionalUploadSignatureRef.current = null;
      setAdditionalErrors({});
      setErrors({});
      setSelectedOption(dropdownCurrentOption || "");
    }
  }, [isOpen, type, dropdownCurrentOption]);

  useEffect(() => {
    if (!isOpen) return;
    if (type !== "addPassenger") return;

    onPersistDraft?.(passengerDraft as Partial<PassengerFormValues>);
  }, [isOpen, type, passengerDraft, onPersistDraft]);

  useEffect(() => {
    if (!isOpen) return;
    if (type !== "addCrew") return;

    onPersistDraft?.(crewDraft as Partial<CrewFormSchema>);
  }, [isOpen, type, crewDraft, onPersistDraft]);

  useEffect(() => {
    if (!isOpen) return;
    if (type !== "addPortCall") return;

    onPersistDraft?.(portCallDraft as Partial<PortCallsSchema>);
  }, [isOpen, type, portCallDraft, onPersistDraft]);

  const handleFileUpload = ({
    newFiles,
    setUploadErrors,
    setUploadFiles,
    onUploadDocument,
    uploadTracker,
  }: {
    newFiles: File[];
    setUploadErrors: (
      value: { files?: string } | ((prev: { files?: string }) => { files?: string }),
    ) => void;
    setUploadFiles: (files: UploadEntry[]) => void;
    onUploadDocument?: (file: File) => void;
    uploadTracker: React.MutableRefObject<string | null>;
  }) => {
    if (newFiles.length > 1) {
      setUploadErrors({ files: t("upload.one-document") });
      return;
    }

    const file = newFiles[0];
    if (!file) {
      return;
    }

    setUploadErrors((prev) => ({ ...prev, files: undefined }));
    setUploadFiles([file]);
    triggerUpload(file, onUploadDocument, uploadTracker);
  };

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={handleDrawerClose}
      container={typeof window !== "undefined" ? () => document.body : undefined}
      ModalProps={{
        keepMounted: true,
        container: typeof window !== "undefined" ? document.body : undefined,
        sx: { zIndex: styles.drawer.zIndex },
      }}
      slotProps={{
        backdrop: { sx: styles.backdrop },
        paper: {
          sx: {
            ...styles.drawer,
            display: "flex",
            flexDirection: "column",
          },
        },
      }}
    >
      <div
        className="flex flex-col h-full max-h-screen overflow-auto"
        data-testid={`${TEST_ID_BASE}-wrapper`}
      >
        <div className="p-6">
          <div className="flex items-center justify-between">
            <h6 className="font-extrabold text-xl" style={styles.headerText}>
              {title}
            </h6>
            <IconButton
              onClick={handleCancelClick}
              size="small"
              className="text-gray-500"
              data-testid={`${TEST_ID_BASE}-close-icon`}
            >
              <CloseIcon />
            </IconButton>
          </div>
          {!!type &&
            type !== "resubmit" &&
            type !== "suspend" &&
            type !== "details" &&
            type !== "permanentReject" &&
            type !== "notifications" &&
            type !== "addCrew" &&
            type !== "editCrew" &&
            type !== "addPassenger" &&
            type !== "editPassenger" &&
            type !== "statusDetails" && (
              <div className="mt-6">
                <p style={styles.descriptionText}>{drawerDescription}</p>
                {requestId && <span style={styles.idText}>#{requestId}.</span>}
              </div>
            )}
          {type === "resubmit" && (
            <div className="mt-6">
              <p style={styles.descriptionText}>
                {drawerDescription} <span style={styles.idText}>#{requestId}.</span>
              </p>
            </div>
          )}
          {type === "notifications" && (
            <div className="font-bold mt-6">
              <p style={styles.descriptionText}>{drawerDescription}</p>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-hidden">
          <div className="h-full overflow-y-auto px-6 space-y-6 pb-6">
            {type === "status" && (
              <div className="col-span-full mt-6">
                <CustomFormLabel data-testid="statusLabelId">{dropdownLabel}</CustomFormLabel>
                <Select
                  options={dropdownOptions || []}
                  value={selectedOption}
                  onChange={(val) => {
                    setSelectedOption(val);
                    setErrors((prev) => ({ ...prev, selectedOption: undefined }));
                  }}
                  testId={`${TEST_ID_BASE}-status-select`}
                  placeholder={dropdownLabel}
                  style={{ width: "100%", marginTop: "4px" }}
                />
                {errors.selectedOption && (
                  <p style={styles.errorText} className="text-sm mt-1">
                    {errors.selectedOption}
                  </p>
                )}
              </div>
            )}

            {type === "details" && (
              <div className="col-span-full mb-0 flex flex-col gap-2">
                <CustomFormLabel sx={{ fontSize: 16, fontWeight: 700 }} data-testid="labelId">
                  <span style={{ whiteSpace: "pre-line" }}>{notesLabel}</span>
                </CustomFormLabel>
                <div
                  className="text-sm whitespace-pre-wrap wrap-break-word"
                  data-testid={`${TEST_ID_BASE}-static-content`}
                >
                  {staticContent?.trim()
                    ? staticContent
                    : detailsAttachments && detailsAttachments.length > 0
                      ? ""
                      : "-"}
                </div>
                {detailsAttachments && detailsAttachments.length > 0 && (
                  <div className="mt-6">
                    <h2 className="text-base text-[#646A77] font-medium mb-2">
                      {t("security-clearance.attachments")}
                    </h2>
                    <div className="border rounded p-3 w-full border-[#F1F2F4]">
                      {detailsAttachments.map((ref, idx) => {
                        const { url, displayName } = resolveSideDrawerAttachment(ref);
                        return (
                          <div
                            key={url + idx}
                            className="flex justify-between items-center mb-2 last:mb-0"
                          >
                            <a
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 underline break-all cursor-pointer"
                              onClick={(e) => {
                                e.preventDefault();
                                if (onPreview) {
                                  onPreview(url);
                                }
                              }}
                            >
                              {displayName}
                            </a>
                            <button
                              type="button"
                              onClick={async () =>
                                onAttachmentDownload && (await onAttachmentDownload(url))
                              }
                              className="cursor-pointer"
                              title={t("common.download")}
                            >
                              <FileDownloadOutlined />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {detailsHistory && detailsHistory.length > 0 && (
                  <div className="mt-6" data-testid="side-drawer-history">
                    <Accordion
                      testId="side-drawer-history-accordion"
                      summary={t("security-clearance.history", "History")}
                    >
                      <div className="space-y-4 pt-4">
                        {detailsHistory.map((item, idx) => {
                          const reason = item.taskDecision?.reason || "-";
                          const attachments = item.taskDecision?.attachments || [];
                          const status = item.taskDecision?.status;
                          const submittedBy = item.taskDecision?.submittedBy;
                          const createdOn = item.createdOn;
                          return (
                            <div
                              key={(item.createdOn || "") + idx}
                              className="border rounded-md p-3 border-[#F1F2F4]"
                            >
                              <div className="text-sm font-medium mb-2 flex flex-wrap gap-2">
                                {status && (
                                  <span>
                                    {t(`security-clearance.status-labels.${status}`, status)}
                                  </span>
                                )}
                                {submittedBy && <span>- {submittedBy}</span>}
                                {createdOn && (
                                  <span className="text-[#646A77]">
                                    {new Date(createdOn).toLocaleString([], {
                                      timeZone: "UTC",
                                      hour12: false,
                                    })}
                                  </span>
                                )}
                              </div>
                              <div className="text-sm whitespace-pre-wrap mb-2">{reason}</div>
                              {attachments.length > 0 && (
                                <div className="mt-2">
                                  <h3 className="text-sm font-semibold mb-2">
                                    {t("security-clearance.attachments")}
                                  </h3>
                                  <div>
                                    {attachments.map((ref, aIdx) => {
                                      const { url, displayName } = resolveSideDrawerAttachment(ref);
                                      return (
                                        <div
                                          key={url + aIdx}
                                          className="flex justify-between items-center mb-2 last:mb-0"
                                        >
                                          <span className="break-all text-[#111]">
                                            {displayName}
                                          </span>
                                          <button
                                            type="button"
                                            onClick={async () =>
                                              onAttachmentDownload &&
                                              (await onAttachmentDownload(url))
                                            }
                                            className="cursor-pointer"
                                            title={t("common.download")}
                                          >
                                            <FileDownloadOutlined />
                                          </button>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </Accordion>
                  </div>
                )}
              </div>
            )}

            {(type === "notifications" || type === "statusDetails") && (
              <div className="col-span-full mb-0 flex flex-col gap-2">
                <CustomFormLabel sx={{ fontSize: 16, fontWeight: 700 }} data-testid="labelId">
                  {externalNotes || externalNotesDocument ? externalNotesLabel : null}
                </CustomFormLabel>

                <div
                  className="text-sm whitespace-pre-wrap wrap-break-word"
                  data-testid={`${TEST_ID_BASE}-notes-content`}
                >
                  {externalNotes || externalNotesDocument ? externalNotes || "-" : null}
                </div>
              </div>
            )}

            {type === "statusDetails" && externalNotesDocument && (
              <div className="mb-2">
                <h2 className="text-base text-[#646A77] mt-8 font-medium">
                  {t(`vessel-management.vessel-status-page.attachments`)}
                </h2>
              </div>
            )}

            {(type === "notifications" || type === "statusDetails") && externalNotesDocument && (
              <div className="border rounded p-3 w-full border-[#F1F2F4] h-16">
                <h3 className="text-base mb-2">
                  {type === "notifications" && t("security-clearance.attachments")}
                </h3>
                <div className="flex justify-between items-center mb-2 w-full">
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline break-all cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      if (onPreview) {
                        onPreview(externalNotesDocument);
                      } else {
                        window.open(externalNotesDocument, "_blank");
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        if (onPreview) {
                          onPreview(externalNotesDocument);
                        } else {
                          window.open(externalNotesDocument, "_blank");
                        }
                      }
                    }}
                    tabIndex={0}
                  >
                    {externalNotesDocumentName || externalNotesDocument}
                  </a>
                  {onDownload ? (
                    <button onClick={onDownload} className="cursor-pointer" title="Download">
                      <FileDownloadOutlined />
                    </button>
                  ) : null}
                </div>
              </div>
            )}
            {type === "notifications" && cancelAttachment && (
              <div className="border rounded p-3 w-full border-[#F1F2F4] mt-4">
                <h3 className="text-base mb-2">{type === "notifications" && "Attachment"}</h3>
                <div className="flex justify-between items-center mb-2 w-full">
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline break-all cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      if (onPreview) {
                        onPreview(cancelAttachment.documentUrl);
                      } else {
                        window.open(cancelAttachment.documentUrl, "_blank");
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        if (onPreview) {
                          onPreview(cancelAttachment.documentUrl);
                        } else {
                          window.open(cancelAttachment.documentUrl, "_blank");
                        }
                      }
                    }}
                    tabIndex={0}
                  >
                    {cancelAttachment.documentName}
                  </a>
                  {onDownload ? (
                    <button onClick={onDownload} className="cursor-pointer" title="Download">
                      <FileDownloadOutlined />
                    </button>
                  ) : null}
                </div>
              </div>
            )}
            {((type === "approval" && !showChangeValidityPeriod) ||
              type === "reject" ||
              type === "status" ||
              type === "suspend" ||
              type === "permanentReject") && (
              <div className="col-span-full mb-0 flex flex-col gap-2">
                <CustomFormLabel data-testid="labelId">
                  {hasExternalInternal ? externalNotesLabel : notesLabel}
                </CustomFormLabel>
                <Input
                  placeholder={hasExternalInternal ? externalNotesLabel : notesLabel}
                  multiline
                  minRows={4}
                  fullWidth
                  variant="outlined"
                  value={description}
                  onKeyDown={(e) => {
                    if (!isPrintableKey(e)) return;
                    if (description.length >= TEXTAREA_MAX_CHARS) {
                      setErrors((prev) => ({
                        ...prev,
                        description: t("form-validation.max-length", {
                          label: hasExternalInternal ? externalNotesLabel : notesLabel,
                          max: TEXTAREA_MAX_CHARS,
                        }),
                      }));
                    }
                  }}
                  onBlur={() => {
                    const trimmedDescription = normalizeDrawerText(description);
                    setDescription(trimmedDescription);
                    const res = descriptionOnlySchema.safeParse({
                      description: trimmedDescription,
                    });
                    setErrors((prev) => ({
                      ...prev,
                      description: res.success
                        ? undefined
                        : res.error.flatten().fieldErrors.description?.[0],
                    }));
                  }}
                  onChange={(e) => {
                    const val = e.target.value;
                    setDescription(val);
                    const res = descriptionOnlySchema.safeParse({ description: val });
                    setErrors((prev) => ({
                      ...prev,
                      description: res.success
                        ? undefined
                        : res.error.flatten().fieldErrors.description?.[0],
                    }));
                  }}
                  slotProps={inputSlotProps}
                  sx={sxProps}
                  testId={`${TEST_ID_BASE}-notes-input`}
                />
                {errors.description && (
                  <p style={styles.errorText} className="text-sm mt-1">
                    {errors.description}
                  </p>
                )}
                <FormHelperText sx={charCounterSx}>
                  {description.length}/{TEXTAREA_MAX_CHARS}
                </FormHelperText>
              </div>
            )}
            {type === "approval" && showChangeValidityPeriod && (
              <div className="col-span-full mt-6">
                <CustomFormLabel data-testid="statusLabelId">{dropdownLabel}</CustomFormLabel>
                <Select
                  options={dropdownOptions || []}
                  value={selectedOption}
                  onChange={(val) => {
                    setSelectedOption(val);
                    setErrors((prev) => ({ ...prev, selectedOption: undefined }));
                  }}
                  testId={`${TEST_ID_BASE}-status-select`}
                  placeholder={dropdownLabel}
                  style={{ width: "100%", marginTop: "4px" }}
                />
                {errors.selectedOption && (
                  <p style={styles.errorText} className="text-sm mt-1">
                    {errors.selectedOption}
                  </p>
                )}
              </div>
            )}
            {type === "resubmit" && (
              <div className="col-span-full mb-0 flex flex-col gap-2">
                <CustomFormLabel data-testid="labelId">{notesLabel}</CustomFormLabel>
                <Input
                  placeholder={notesLabel}
                  multiline
                  minRows={4}
                  fullWidth
                  variant="outlined"
                  value={mandatoryDescription}
                  onKeyDown={(e) => {
                    if (!isPrintableKey(e)) return;
                    if (mandatoryDescription.length >= TEXTAREA_MAX_CHARS) {
                      setErrors((prev) => ({
                        ...prev,
                        mandatoryDescription: t("form-validation.max-length", {
                          label: notesLabel,
                          max: TEXTAREA_MAX_CHARS,
                        }),
                      }));
                    }
                  }}
                  onBlur={() => {
                    const trimmedDescription = normalizeDrawerText(mandatoryDescription);
                    setMandatoryDescription(trimmedDescription);
                    const res = mandatoryDescriptionOnlySchema.safeParse({
                      mandatoryDescription: trimmedDescription,
                    });
                    setErrors((prev) => ({
                      ...prev,
                      mandatoryDescription: res.success
                        ? undefined
                        : res.error.flatten().fieldErrors.mandatoryDescription?.[0],
                    }));
                  }}
                  onChange={(e) => {
                    const val = e.target.value;
                    setMandatoryDescription(val);
                    const res = mandatoryDescriptionOnlySchema.safeParse({
                      mandatoryDescription: val,
                    });
                    setErrors((prev) => ({
                      ...prev,
                      mandatoryDescription: res.success
                        ? undefined
                        : res.error.flatten().fieldErrors.mandatoryDescription?.[0],
                    }));
                  }}
                  slotProps={inputSlotProps}
                  sx={sxProps}
                  testId={`${TEST_ID_BASE}-notes-input`}
                />
                {errors.mandatoryDescription && (
                  <p style={styles.errorText} className="text-sm mt-1">
                    {errors.mandatoryDescription}
                  </p>
                )}
                <FormHelperText sx={charCounterSx}>
                  {mandatoryDescription.length}/{TEXTAREA_MAX_CHARS}
                </FormHelperText>
              </div>
            )}
            {showsPrimaryDocumentUpload && (
              <div className="col-span-full mt-6">
                <input type="hidden" name="sideDrawerApprovalFileUrl" />
                <div className={errors.files ? "border border-red-500 rounded-md p-1" : ""}>
                  <DocumentUpload
                    label={
                      externalAttachmentUploadTitle
                        ? externalAttachmentUploadTitle
                        : fileUploadTitle || t(`${LANG_BASE}.attachments`)
                    }
                    testId="approval-file-upload"
                    name="sideDrawerApprovalFile"
                    isSideDrawer
                    maxFiles={1}
                    allowedExtensions={allowedSideDrawerExtensions}
                    onRemoveFile={() => {
                      setErrors((prev) => ({ ...prev, files: undefined }));
                      setSideDrawerApprovalFile([]);
                      approvalUploadSignatureRef.current = null;
                      onRemoveUploadedAttachmentUrl?.();
                    }}
                    onUpload={(newFiles) =>
                      handleFileUpload({
                        newFiles,
                        setUploadErrors: setErrors,
                        setUploadFiles: setSideDrawerApprovalFile,
                        onUploadDocument: onDocumentUpload,
                        uploadTracker: approvalUploadSignatureRef,
                      })
                    }
                    conditions={FILE_UPLOAD_PROFILES.sideDrawerApprovalFile}
                    files={sideDrawerApprovalFile}
                    setFiles={setSideDrawerApprovalFile}
                    uploadDescription={t("upload.dragOrDropDescription")}
                    disabled={isExternalDocumentUploadPending}
                  />
                </div>
                {errors.files && (
                  <p style={styles.errorText} className="text-sm mt-1">
                    {errors.files}
                  </p>
                )}

                {sideDrawerApprovalFile[0] && !errors.files && (
                  <div className="flex-1 mt-4">
                    <SingleFile
                      file={sideDrawerApprovalFile[0]}
                      fieldName="sideDrawerApprovalFile"
                      setFiles={() => {
                        setErrors((prev) => ({ ...prev, files: undefined }));
                        setSideDrawerApprovalFile([]);
                        approvalUploadSignatureRef.current = null;
                        onRemoveUploadedAttachmentUrl?.();
                      }}
                      isLoading={isExternalDocumentUploadPending}
                    />
                  </div>
                )}
              </div>
            )}

            {type === "statusDetails" &&
              (externalNotes || externalNotesDocument) &&
              (internalNotesDocument || internalNotes) && (
                <div className="my-8">
                  <Divider />
                </div>
              )}

            {((type === "approval" && hasExternalInternal) || type === "reject") && <Divider />}

            {((type === "approval" && hasExternalInternal) || type === "reject") && (
              <div className="col-span-full mb-0 flex flex-col gap-2 mt-6">
                <CustomFormLabel data-testid="labelId">{internalNotesLabel}</CustomFormLabel>
                <Input
                  placeholder={internalNotesLabel}
                  multiline
                  minRows={4}
                  fullWidth
                  variant="outlined"
                  value={additionalDescription}
                  onKeyDown={(e) => {
                    if (!isPrintableKey(e)) return;
                    if (additionalDescription.length >= TEXTAREA_MAX_CHARS) {
                      setAdditionalErrors((prev) => ({
                        ...prev,
                        additionalDescription: t("form-validation.max-length", {
                          label: internalNotesLabel,
                          max: TEXTAREA_MAX_CHARS,
                        }),
                      }));
                    }
                  }}
                  onBlur={() => {
                    const trimmedDescription = normalizeDrawerText(additionalDescription);
                    setAdditionalDescription(trimmedDescription);
                    const result = additionalDescriptionOnlySchema.safeParse({
                      additionalDescription: trimmedDescription,
                    });
                    setAdditionalErrors((prev) => ({
                      ...prev,
                      additionalDescription: result.success
                        ? undefined
                        : result.error.flatten().fieldErrors.additionalDescription?.[0],
                    }));
                  }}
                  onChange={(e) => {
                    const value = e.target.value;
                    setAdditionalDescription(value);
                    const result = additionalDescriptionOnlySchema.safeParse({
                      additionalDescription: value,
                    });
                    setAdditionalErrors((prev) => ({
                      ...prev,
                      additionalDescription: result.success
                        ? undefined
                        : result.error.flatten().fieldErrors.additionalDescription?.[0],
                    }));
                  }}
                  slotProps={inputSlotProps}
                  sx={sxProps}
                  testId={`${TEST_ID_BASE}-additional-notes-input`}
                />
                {additionalErrors.additionalDescription && (
                  <p style={styles.errorText} className="text-sm mt-1">
                    {additionalErrors.additionalDescription}
                  </p>
                )}
                {errors.description && (
                  <p style={styles.errorText} className="text-sm mt-1">
                    {errors.description}
                  </p>
                )}
                <FormHelperText sx={charCounterSx}>
                  {additionalDescription.length}/{TEXTAREA_MAX_CHARS}
                </FormHelperText>
              </div>
            )}

            {((type === "notifications" && internalNotes) ||
              (type === "statusDetails" && (internalNotes || internalNotesDocument))) && (
              <div className="col-span-full mb-0 flex flex-col gap-2">
                <CustomFormLabel sx={{ fontSize: 16, fontWeight: 700 }} data-testid="labelId">
                  {internalNotesLabel}
                </CustomFormLabel>
                <div
                  className="text-sm whitespace-pre-wrap wrap-break-word"
                  data-testid={`${TEST_ID_BASE}-additionalNotes-content`}
                >
                  {internalNotes || internalNotesDocument ? internalNotes || "-" : null}
                </div>
              </div>
            )}

            {type === "statusDetails" && internalNotesDocument && (
              <div className="mb-2">
                <h2 className="text-base text-[#646A77] mt-8 font-medium">
                  {t(`vessel-management.vessel-status-page.attachments`)}
                </h2>
              </div>
            )}

            {(type === "notifications" || type === "statusDetails") && internalNotesDocument && (
              <div className="border rounded p-3 w-full border-[#F1F2F4] h-16">
                <h3 className="text-base mb-2">
                  {type === "notifications" && t("security-clearance.attachments")}
                </h3>

                <div className="flex justify-between items-center mb-2 w-full">
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline break-all cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      if (onAdditionalPreview) {
                        onAdditionalPreview();
                      } else {
                        window.open(internalNotesDocument ?? "", "_blank");
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        if (onAdditionalPreview) {
                          onAdditionalPreview();
                        } else {
                          window.open(internalNotesDocument ?? "", "_blank");
                        }
                      }
                    }}
                    tabIndex={0}
                  >
                    {internalNotesDocumentName || internalNotesDocument}
                  </a>
                  {onAdditionalDownload || onDownload ? (
                    <button
                      onClick={onAdditionalDownload || onDownload}
                      className="cursor-pointer"
                      title="Download"
                    >
                      <FileDownloadOutlined />
                    </button>
                  ) : null}
                </div>
              </div>
            )}
            {((type === "approval" && hasExternalInternal) || type === "reject") && (
              <div className="col-span-full mt-6 ">
                <input type="hidden" name="sideDrawerAdditionalFileUrl" />
                <div
                  className={additionalErrors.files ? "border border-red-500 rounded-md p-1" : ""}
                >
                  <DocumentUpload
                    label={
                      internalAttachmentUploadTitle
                        ? internalAttachmentUploadTitle
                        : additionalFileUploadTitle
                    }
                    testId={`${TEST_ID_BASE}-additional-file-upload`}
                    name="sideDrawerAdditionalFile"
                    isSideDrawer
                    maxFiles={1}
                    allowedExtensions={allowedSideDrawerExtensions}
                    onRemoveFile={() => {
                      setAdditionalErrors((prev) => ({ ...prev, files: undefined }));
                      setSideDrawerAdditionalFile([]);
                      additionalUploadSignatureRef.current = null;
                      onRemoveUploadedAttachmentUrl?.();
                    }}
                    onUpload={(newFiles) =>
                      handleFileUpload({
                        newFiles,
                        setUploadErrors: setAdditionalErrors,
                        setUploadFiles: setSideDrawerAdditionalFile,
                        onUploadDocument: onAdditionalDocumentUpload,
                        uploadTracker: additionalUploadSignatureRef,
                      })
                    }
                    conditions={FILE_UPLOAD_PROFILES.sideDrawerApprovalFile}
                    files={sideDrawerAdditionalFile}
                    setFiles={setSideDrawerAdditionalFile}
                    uploadDescription={t("upload.dragOrDropDescription")}
                    disabled={isInternalDocumentUploadPending}
                  />
                </div>
                {additionalErrors.files && (
                  <p style={styles.errorText} className="text-sm mt-1">
                    {additionalErrors.files}
                  </p>
                )}

                {sideDrawerAdditionalFile[0] && !additionalErrors.files && (
                  <div className="flex-1 mt-4">
                    <SingleFile
                      file={sideDrawerAdditionalFile[0]}
                      fieldName="sideDrawerAdditionalFile"
                      setFiles={() => {
                        setAdditionalErrors((prev) => ({ ...prev, files: undefined }));
                        setSideDrawerAdditionalFile([]);
                        additionalUploadSignatureRef.current = null;
                        onRemoveUploadedAttachmentUrl?.();
                      }}
                      isLoading={isInternalDocumentUploadPending}
                    />
                  </div>
                )}
              </div>
            )}

            {(type === "addPortCall" || type === "editPortCall") && (
              <div className="flex flex-col gap-3">
                <Controller
                  name="port"
                  control={portCallForm.control}
                  render={({ field }) => (
                    <div className="label-and-input flex flex-col gap-2">
                      <CustomFormLabel data-testid="labelId">
                        {t("vessel-call.last-ten-ports.sidebar.fields.port")}
                      </CustomFormLabel>

                      <Autocomplete
                        options={portsOptionsPortCall}
                        value={
                          field.value
                            ? (portsOptionsPortCall.find(
                                (o: AutocompleteOption) => o.value === field.value,
                              ) ?? null)
                            : null
                        }
                        onChange={handlePortCallPortChange}
                        testId={`${TEST_ID_BASE}-port-select`}
                        placeholder={t("vessel-call.last-ten-ports.sidebar.fields.port")}
                        maxHeight={200}
                        error={!!portCallForm.formState.errors.port}
                      />

                      {portCallForm.formState.errors.port && (
                        <p style={styles.errorText} className="text-sm mt-1">
                          {portCallForm.formState.errors.port.message}
                        </p>
                      )}
                    </div>
                  )}
                />

                <Controller
                  name="country"
                  control={portCallForm.control}
                  render={({ field }) => (
                    <div className="label-and-input flex flex-col gap-2">
                      <CustomFormLabel data-testid="labelId">
                        {t("vessel-call.last-ten-ports.sidebar.fields.country")}
                      </CustomFormLabel>

                      <Autocomplete
                        options={countriesOptionsPortCall}
                        value={
                          field.value
                            ? (countriesOptionsPortCall.find(
                                (o: AutocompleteOption) => o.value === field.value,
                              ) ?? null)
                            : null
                        }
                        onChange={handlePortCallCountryChange}
                        testId={`${TEST_ID_BASE}-country-select`}
                        placeholder={t("vessel-call.last-ten-ports.sidebar.fields.country")}
                        maxHeight={200}
                        error={!!portCallForm.formState.errors.country}
                      />

                      {portCallForm.formState.errors.country && (
                        <p style={styles.errorText} className="text-sm mt-1">
                          {portCallForm.formState.errors.country.message}
                        </p>
                      )}
                    </div>
                  )}
                />

                <Controller
                  name="securityLevelOfPortCall"
                  control={portCallForm.control}
                  render={({ field }) => (
                    <div className="label-and-input flex flex-col gap-2">
                      <CustomFormLabel data-testid="labelId">
                        {t("vessel-call.last-ten-ports.sidebar.fields.security-level")}
                      </CustomFormLabel>

                      <Autocomplete
                        options={securityLevelOptions}
                        value={
                          field.value
                            ? (securityLevelOptions.find(
                                (o) => String(o.value) === String(field.value),
                              ) ?? null)
                            : null
                        }
                        onChange={(opt) =>
                          field.onChange(opt?.value != null ? String(opt.value) : "")
                        }
                        testId={`${TEST_ID_BASE}-securityLevelOfPortCall-select`}
                        placeholder={t("vessel-call.last-ten-ports.sidebar.fields.security-level")}
                        maxHeight={200}
                        error={!!portCallForm.formState.errors.securityLevelOfPortCall}
                      />

                      {portCallForm.formState.errors.securityLevelOfPortCall && (
                        <p style={styles.errorText} className="text-sm mt-1">
                          {portCallForm.formState.errors.securityLevelOfPortCall.message}
                        </p>
                      )}
                    </div>
                  )}
                />

                <Controller
                  name="arrivalDate"
                  control={portCallForm.control}
                  render={({ field }) => (
                    <div className="label-and-input flex flex-col gap-2">
                      <CustomFormLabel data-testid="labelId">
                        {t("vessel-call.last-ten-ports.sidebar.fields.arrival-date")}
                      </CustomFormLabel>

                      <DatePicker
                        hasError={!!portCallForm.formState.errors.arrivalDate}
                        value={asDateOrNull(field.value)}
                        onChange={(d) => {
                          field.onChange(d ?? "");
                          validatePortCallWithContext(["arrivalDate", "departureDate"]);
                        }}
                        testId={`${TEST_ID_BASE}-arrival-date-input`}
                        timePicker={false}
                        dateConstraint="not-future"
                        maxDate={portCallArrivalMaxDate}
                      />

                      {portCallForm.formState.errors.arrivalDate && (
                        <p style={styles.errorText} className="text-sm mt-1">
                          {portCallForm.formState.errors.arrivalDate.message}
                        </p>
                      )}
                    </div>
                  )}
                />

                <Controller
                  name="departureDate"
                  control={portCallForm.control}
                  render={({ field }) => (
                    <div className="label-and-input flex flex-col gap-2">
                      <CustomFormLabel data-testid="labelId">
                        {t("vessel-call.last-ten-ports.sidebar.fields.departure-date")}
                      </CustomFormLabel>

                      <DatePicker
                        hasError={!!portCallForm.formState.errors.departureDate}
                        value={asDateOrNull(field.value)}
                        onChange={(d) => {
                          field.onChange(d ?? "");
                          validatePortCallWithContext(["arrivalDate", "departureDate"]);
                        }}
                        testId={`${TEST_ID_BASE}-departure-date-input`}
                        timePicker={false}
                        dateConstraint="not-future"
                        minDate={portCallDepartureMinDate}
                        maxDate={today}
                      />

                      {portCallForm.formState.errors.departureDate && (
                        <p style={styles.errorText} className="text-sm mt-1">
                          {portCallForm.formState.errors.departureDate.message}
                        </p>
                      )}
                    </div>
                  )}
                />

                <div className="label-and-input flex flex-col gap-2">
                  <CustomFormLabel data-testid="labelId">
                    {t("vessel-call.last-ten-ports.sidebar.fields.internal-notes")}
                  </CustomFormLabel>
                  <Input
                    multiline
                    minRows={4}
                    error={!!portCallForm.formState.errors.comments}
                    variant="outlined"
                    placeholder={t("vessel-call.last-ten-ports.sidebar.fields.internal-notes")}
                    id={`${TEST_ID_BASE}-comments-input`}
                    value={portCallForm.watch("comments")}
                    onChange={(e) => {
                      portCallForm.setValue("comments", e.target.value, {
                        shouldDirty: true,
                        shouldTouch: true,
                        shouldValidate: true,
                      });
                    }}
                    slotProps={inputSlotProps}
                    testId={`${TEST_ID_BASE}-comments-input`}
                    style={portCallForm.formState.errors.comments ? { borderColor: "#d32f2f" } : {}}
                  />
                  {portCallForm.formState.errors.comments && (
                    <p style={styles.errorText} className="text-sm mt-1">
                      {portCallForm.formState.errors.comments.message}
                    </p>
                  )}
                </div>
              </div>
            )}

            {(type === "addCrew" || type === "editCrew") && (
              <div className="flex flex-col gap-3">
                <div className="label-and-input flex flex-col gap-2">
                  <CustomFormLabel data-testid="labelId">
                    {t("vessel-call.create.table-headers.family-name")}
                  </CustomFormLabel>
                  <Input
                    error={!!crewForm.formState.errors.familyName}
                    variant="outlined"
                    placeholder={t("vessel-call.create.table-headers.family-name")}
                    id={`${TEST_ID_BASE}-family-name-input`}
                    value={crewForm.watch("familyName")}
                    onChange={(e) => {
                      const val = e.target.value;
                      crewForm.setValue("familyName", val);
                      crewForm.trigger("familyName");
                      const res = getCrewFormSchema(t).shape.familyName.safeParse(val);
                      setErrors((prev) => ({
                        ...prev,
                        familyName: res.success ? undefined : res.error.message,
                      }));
                    }}
                    slotProps={inputSlotProps}
                    testId={`${TEST_ID_BASE}-family-name-input`}
                    style={crewForm.formState.errors.familyName ? { borderColor: "#d32f2f" } : {}}
                  />
                  {crewForm.formState.errors.familyName && (
                    <p style={styles.errorText} className="text-sm mt-1">
                      {crewForm.formState.errors.familyName.message}
                    </p>
                  )}
                </div>
                <div className="label-and-input flex flex-col gap-2">
                  <CustomFormLabel data-testid="labelId">
                    {t("vessel-call.create.table-headers.given-name")}
                  </CustomFormLabel>
                  <Input
                    error={!!crewForm.formState.errors.givenName}
                    variant="outlined"
                    placeholder={t("vessel-call.create.table-headers.given-name")}
                    id={`${TEST_ID_BASE}-given-name-input`}
                    value={crewForm.watch("givenName")}
                    onChange={(e) => {
                      const val = e.target.value;
                      crewForm.setValue("givenName", val);
                      crewForm.trigger("givenName");
                      const res = getCrewFormSchema(t).shape.givenName.safeParse(val);
                      setErrors((prev) => ({
                        ...prev,
                        givenName: res.success ? undefined : res.error.message,
                      }));
                    }}
                    slotProps={inputSlotProps}
                    testId={`${TEST_ID_BASE}-given-name-input`}
                    style={crewForm.formState.errors.givenName ? { borderColor: "#d32f2f" } : {}}
                  />
                  {crewForm.formState.errors.givenName && (
                    <p style={styles.errorText} className="text-sm mt-1">
                      {crewForm.formState.errors.givenName.message}
                    </p>
                  )}
                </div>
                <Controller
                  name="rank"
                  control={crewForm.control}
                  render={({ field }) => (
                    <div className="label-and-input flex flex-col gap-2">
                      <CustomFormLabel data-testid="labelId">
                        {t("vessel-call.create.table-headers.rank")}
                      </CustomFormLabel>
                      <Autocomplete
                        options={crewRankOptions}
                        value={field.value ? { value: field.value, label: field.value } : null}
                        onChange={(opt) => field.onChange(opt?.value ?? "")}
                        testId={`${TEST_ID_BASE}-rank-select`}
                        placeholder={t("vessel-call.create.table-headers.rank")}
                        maxHeight={200}
                        error={!!crewForm.formState.errors.rank}
                      />
                      {crewForm.formState.errors.rank && (
                        <p style={styles.errorText} className="text-sm mt-1">
                          {crewForm.formState.errors.rank.message}
                        </p>
                      )}
                    </div>
                  )}
                />
                <Controller
                  name="nationality"
                  control={crewForm.control}
                  render={({ field }) => (
                    <div className="label-and-input flex flex-col gap-2">
                      <CustomFormLabel data-testid="labelId">
                        {t("vessel-call.create.table-headers.nationality")}
                      </CustomFormLabel>

                      <Autocomplete
                        options={nationalitiesOptions}
                        value={field.value ? { value: field.value, label: field.value } : null}
                        onChange={(opt) => {
                          crewForm.setValue("nationality", opt?.value ? String(opt.value) : "");
                          crewForm.trigger("nationality");
                        }}
                        testId={`${TEST_ID_BASE}-nationality-select`}
                        placeholder={t("vessel-call.create.table-headers.nationality")}
                        maxHeight={200}
                        error={!!crewForm.formState.errors.nationality}
                      />

                      {crewForm.formState.errors.nationality && (
                        <p style={styles.errorText} className="text-sm mt-1">
                          {crewForm.formState.errors.nationality.message}
                        </p>
                      )}
                    </div>
                  )}
                />
                <Controller
                  name="dateOfBirth"
                  control={crewForm.control}
                  render={({ field }) => (
                    <div className="label-and-input flex flex-col gap-2">
                      <CustomFormLabel data-testid="labelId">
                        {t("vessel-call.create.table-headers.date-of-birth")}
                      </CustomFormLabel>

                      <DatePicker
                        hasError={!!crewForm.formState.errors.dateOfBirth}
                        value={asDateOrNull(field.value)}
                        onChange={(d) => field.onChange(d ?? undefined)}
                        testId={`${TEST_ID_BASE}-date-of-birth-input`}
                        dateConstraint="past"
                        timePicker={false}
                      />

                      {crewForm.formState.errors.dateOfBirth && (
                        <p style={styles.errorText} className="text-sm mt-1">
                          {crewForm.formState.errors.dateOfBirth.message}
                        </p>
                      )}
                    </div>
                  )}
                />
                <div className="label-and-input flex flex-col gap-2">
                  <CustomFormLabel data-testid="labelId">
                    {t("vessel-call.create.table-headers.place-of-birth")}
                  </CustomFormLabel>
                  <Input
                    error={!!crewForm.formState.errors.placeOfBirth}
                    placeholder={t("vessel-call.create.table-headers.place-of-birth")}
                    id={`${TEST_ID_BASE}-place-of-birth-input`}
                    value={crewForm.watch("placeOfBirth")}
                    onChange={(e) => {
                      const val = e.target.value;
                      crewForm.setValue("placeOfBirth", val);
                      crewForm.trigger("placeOfBirth");
                      const res = getCrewFormSchema(t).shape.placeOfBirth.safeParse(val);
                      setErrors((prev) => ({
                        ...prev,
                        placeOfBirth: res.success ? undefined : res.error.message,
                      }));
                    }}
                    testId={`${TEST_ID_BASE}-place-of-birth-input`}
                    style={crewForm.formState.errors.placeOfBirth ? { borderColor: "#d32f2f" } : {}}
                  />
                  {crewForm.formState.errors.placeOfBirth && (
                    <p style={styles.errorText} className="text-sm mt-1">
                      {crewForm.formState.errors.placeOfBirth.message}
                    </p>
                  )}
                </div>
                <div className="label-and-input flex flex-col gap-2">
                  <CustomFormLabel data-testid="labelId">
                    {t("vessel-call.create.table-headers.gender")}
                  </CustomFormLabel>
                  <Controller
                    name="gender"
                    control={crewForm.control}
                    render={({ field }) => (
                      <Autocomplete
                        options={[
                          { value: "M", label: "M" },
                          { value: "F", label: "F" },
                        ]}
                        value={field.value ? { value: field.value, label: field.value } : null}
                        onChange={(opt) => field.onChange(opt?.value ?? "")}
                        error={!!crewForm.formState.errors.gender}
                        placeholder={t("vessel-call.create.table-headers.gender")}
                        maxHeight={200}
                        testId={`${TEST_ID_BASE}-gender-select`}
                      />
                    )}
                  />
                  {crewForm.formState.errors.gender && (
                    <p style={styles.errorText} className="text-sm mt-1">
                      {crewForm.formState.errors.gender.message}
                    </p>
                  )}
                </div>
                <div className="label-and-input flex flex-col gap-2">
                  <CustomFormLabel data-testid="labelId">
                    {t("vessel-call.create.table-headers.passport-number")}
                  </CustomFormLabel>
                  <Input
                    error={!!crewForm.formState.errors.passportNumber}
                    placeholder={t("vessel-call.create.table-headers.passport-number")}
                    id={`${TEST_ID_BASE}-passport-number-input`}
                    value={crewForm.watch("passportNumber")}
                    onChange={(e) => {
                      const val = e.target.value;
                      crewForm.setValue("passportNumber", val);
                      crewForm.trigger("passportNumber");
                      const res = getCrewFormSchema(t).shape.passportNumber.safeParse(val);
                      setErrors((prev) => ({
                        ...prev,
                        passportNumber: res.success ? undefined : res.error.message,
                      }));
                    }}
                    testId={`${TEST_ID_BASE}-passport-number-input`}
                    style={
                      crewForm.formState.errors.passportNumber ? { borderColor: "#d32f2f" } : {}
                    }
                  />
                  {crewForm.formState.errors.passportNumber && (
                    <p style={styles.errorText} className="text-sm mt-1">
                      {crewForm.formState.errors.passportNumber.message}
                    </p>
                  )}
                </div>
                <div className="label-and-input flex flex-col gap-2">
                  <CustomFormLabel data-testid="labelId">
                    {t("vessel-call.create.table-headers.passport-issuing-country")}
                  </CustomFormLabel>
                  <Autocomplete
                    options={issuingStateCountriesOptions}
                    value={(() => {
                      const val = crewForm.watch("issuingState");
                      return val
                        ? (issuingStateCountriesOptions.find(
                            (o: AutocompleteOption) => o.value === val,
                          ) ?? { value: val, label: val })
                        : null;
                    })()}
                    onChange={(option) => {
                      crewForm.setValue("issuingState", option?.value ? String(option.value) : "");
                      crewForm.trigger("issuingState");
                    }}
                    testId={`${TEST_ID_BASE}-issuing-state-select`}
                    placeholder={t("vessel-call.create.table-headers.passport-issuing-country")}
                    maxHeight={200}
                    error={!!crewForm.formState.errors.issuingState}
                  />
                  {crewForm.formState.errors.issuingState && (
                    <p style={styles.errorText} className="text-sm mt-1">
                      {crewForm.formState.errors.issuingState.message}
                    </p>
                  )}
                </div>
                <Controller
                  name="expiryDate"
                  control={crewForm.control}
                  render={({ field }) => (
                    <div className="label-and-input flex flex-col gap-2">
                      <CustomFormLabel data-testid="labelId">
                        {t("vessel-call.create.table-headers.passport-expiry-date")}
                      </CustomFormLabel>

                      <DatePicker
                        hasError={!!crewForm.formState.errors.expiryDate}
                        value={asDateOrNull(field.value)}
                        onChange={(d) => field.onChange(d ?? "")}
                        testId={`${TEST_ID_BASE}-expiry-date-input`}
                        dateConstraint="future"
                        timePicker={false}
                      />

                      {crewForm.formState.errors.expiryDate && (
                        <p style={styles.errorText} className="text-sm mt-1">
                          {crewForm.formState.errors.expiryDate.message}
                        </p>
                      )}
                    </div>
                  )}
                />
              </div>
            )}
            {(type === "addPassenger" || type === "editPassenger") && (
              <div className="flex flex-col gap-3">
                <div className="label-and-input flex flex-col gap-2">
                  <CustomFormLabel data-testid="labelId">
                    {t("vessel-call.create.table-headers.family-name")}
                  </CustomFormLabel>
                  <Input
                    error={!!passengerForm.formState.errors.familyName}
                    variant="outlined"
                    placeholder={t("vessel-call.create.table-headers.family-name")}
                    id={`${TEST_ID_BASE}-family-name-input`}
                    value={passengerForm.watch("familyName")}
                    onChange={(e) => {
                      const val = e.target.value;
                      passengerForm.setValue("familyName", val);
                      passengerForm.trigger("familyName");
                    }}
                    required
                    slotProps={inputSlotProps}
                    testId={`${TEST_ID_BASE}-family-name-input`}
                    style={
                      passengerForm.formState.errors.familyName ? { borderColor: "#d32f2f" } : {}
                    }
                  />
                  {passengerForm.formState.errors.familyName && (
                    <p style={styles.errorText} className="text-sm mt-1">
                      {passengerForm.formState.errors.familyName.message}
                    </p>
                  )}
                </div>
                <div className="label-and-input flex flex-col gap-2">
                  <CustomFormLabel data-testid="labelId">
                    {t("vessel-call.create.table-headers.given-name")}
                  </CustomFormLabel>
                  <Input
                    error={!!passengerForm.formState.errors.givenName}
                    variant="outlined"
                    placeholder={t("vessel-call.create.table-headers.given-name")}
                    id={`${TEST_ID_BASE}-given-name-input`}
                    value={passengerForm.watch("givenName")}
                    onChange={(e) => {
                      const val = e.target.value;
                      passengerForm.setValue("givenName", val);
                      passengerForm.trigger("givenName");
                    }}
                    required
                    slotProps={inputSlotProps}
                    testId={`${TEST_ID_BASE}-given-name-input`}
                    style={
                      passengerForm.formState.errors.givenName ? { borderColor: "#d32f2f" } : {}
                    }
                  />
                  {passengerForm.formState.errors.givenName && (
                    <p style={styles.errorText} className="text-sm mt-1">
                      {passengerForm.formState.errors.givenName.message}
                    </p>
                  )}
                </div>
                <div className="label-and-input flex flex-col gap-2">
                  <CustomFormLabel data-testid="labelId">
                    {t("vessel-call.create.table-headers.nationality")}
                  </CustomFormLabel>
                  <Autocomplete
                    options={nationalitiesOptions}
                    value={(() => {
                      const val = passengerForm.watch("nationality");
                      return val ? { value: val, label: val } : null;
                    })()}
                    onChange={(option) => {
                      passengerForm.setValue(
                        "nationality",
                        option?.value ? String(option.value) : "",
                      );
                      passengerForm.trigger("nationality");
                    }}
                    testId={`${TEST_ID_BASE}-nationality-select`}
                    placeholder={t("vessel-call.create.table-headers.nationality")}
                    maxHeight={200}
                    error={!!passengerForm.formState.errors.nationality}
                  />
                  {passengerForm.formState.errors.nationality && (
                    <p style={styles.errorText} className="text-sm mt-1">
                      {passengerForm.formState.errors.nationality.message}
                    </p>
                  )}
                </div>
                <Controller
                  name="dateOfBirth"
                  control={passengerForm.control}
                  render={({ field }) => (
                    <div className="label-and-input flex flex-col gap-2">
                      <CustomFormLabel data-testid="labelId">
                        {t("vessel-call.create.table-headers.date-of-birth")}
                      </CustomFormLabel>

                      <DatePicker
                        hasError={!!passengerForm.formState.errors.dateOfBirth}
                        value={asDateOrNull(field.value)}
                        onChange={(d) => field.onChange(d ?? undefined)}
                        testId={`${TEST_ID_BASE}-date-of-birth-input`}
                        dateConstraint="past"
                        timePicker={false}
                      />

                      {passengerForm.formState.errors.dateOfBirth && (
                        <p style={styles.errorText} className="text-sm mt-1">
                          {passengerForm.formState.errors.dateOfBirth.message}
                        </p>
                      )}
                    </div>
                  )}
                />
                <div className="label-and-input flex flex-col gap-2">
                  <CustomFormLabel data-testid="labelId">
                    {t("vessel-call.create.table-headers.place-of-birth")}
                  </CustomFormLabel>
                  <Input
                    error={!!passengerForm.formState.errors.placeOfBirth}
                    placeholder={t("vessel-call.create.table-headers.place-of-birth")}
                    id={`${TEST_ID_BASE}-place-of-birth-input`}
                    value={passengerForm.watch("placeOfBirth")}
                    onChange={(e) => {
                      const val = e.target.value;
                      passengerForm.setValue("placeOfBirth", val);
                      passengerForm.trigger("placeOfBirth");
                    }}
                    required
                    testId={`${TEST_ID_BASE}-place-of-birth-input`}
                    style={
                      passengerForm.formState.errors.placeOfBirth ? { borderColor: "#d32f2f" } : {}
                    }
                  />
                  {passengerForm.formState.errors.placeOfBirth && (
                    <p style={styles.errorText} className="text-sm mt-1">
                      {passengerForm.formState.errors.placeOfBirth.message}
                    </p>
                  )}
                </div>
                <Controller
                  name="gender"
                  control={passengerForm.control}
                  render={({ field }) => (
                    <div className="label-and-input flex flex-col gap-2">
                      <CustomFormLabel data-testid="labelId">
                        {t("vessel-call.create.table-headers.gender")}
                      </CustomFormLabel>

                      <Autocomplete
                        options={[
                          { value: "M", label: "M" },
                          { value: "F", label: "F" },
                        ]}
                        value={field.value ? { value: field.value, label: field.value } : null}
                        onChange={(opt) => field.onChange(opt?.value ?? "")}
                        testId={`${TEST_ID_BASE}-gender-select`}
                        placeholder={t("vessel-call.create.table-headers.gender")}
                        maxHeight={200}
                        error={!!passengerForm.formState.errors.gender}
                      />
                      {passengerForm.formState.errors.gender && (
                        <p style={styles.errorText} className="text-sm mt-1">
                          {passengerForm.formState.errors.gender.message}
                        </p>
                      )}
                    </div>
                  )}
                />
                <div className="label-and-input flex flex-col gap-2">
                  <CustomFormLabel data-testid="labelId">
                    {t("vessel-call.create.table-headers.passport-number")}
                  </CustomFormLabel>
                  <Input
                    error={!!passengerForm.formState.errors.identityDocumentNumber}
                    placeholder={t("vessel-call.create.table-headers.passport-number")}
                    id={`${TEST_ID_BASE}-identity-number-input`}
                    value={passengerForm.watch("identityDocumentNumber")}
                    onChange={(e) => {
                      const val = e.target.value;
                      passengerForm.setValue("identityDocumentNumber", val);
                      passengerForm.trigger("identityDocumentNumber");
                    }}
                    required
                    testId={`${TEST_ID_BASE}-identity-number-input`}
                    style={
                      passengerForm.formState.errors.identityDocumentNumber
                        ? { borderColor: "#d32f2f" }
                        : {}
                    }
                  />
                  {passengerForm.formState.errors.identityDocumentNumber && (
                    <p style={styles.errorText} className="text-sm mt-1">
                      {passengerForm.formState.errors.identityDocumentNumber.message}
                    </p>
                  )}
                </div>
                <div className="label-and-input flex flex-col gap-2">
                  <CustomFormLabel data-testid="labelId">
                    {t("vessel-call.create.table-headers.passport-issuing-country")}
                  </CustomFormLabel>
                  <Autocomplete
                    options={issuingStateCountriesOptions}
                    value={(() => {
                      const val = passengerForm.watch("issuingState");
                      return val
                        ? (issuingStateCountriesOptions.find(
                            (o: AutocompleteOption) => o.value === val,
                          ) ?? { value: val, label: val })
                        : null;
                    })()}
                    onChange={(option) => {
                      passengerForm.setValue(
                        "issuingState",
                        option?.value ? String(option.value) : "",
                      );
                      passengerForm.trigger("issuingState");
                    }}
                    testId={`${TEST_ID_BASE}-issuing-state-select`}
                    placeholder={t("vessel-call.create.table-headers.passport-issuing-country")}
                    maxHeight={200}
                    error={!!passengerForm.formState.errors.issuingState}
                  />
                  {passengerForm.formState.errors.issuingState && (
                    <p style={styles.errorText} className="text-sm mt-1">
                      {passengerForm.formState.errors.issuingState.message}
                    </p>
                  )}
                </div>
                <Controller
                  name="expiryDate"
                  control={passengerForm.control}
                  render={({ field }) => (
                    <div className="label-and-input flex flex-col gap-2">
                      <CustomFormLabel data-testid="labelId">
                        {t("vessel-call.create.table-headers.passport-expiry-date")}
                      </CustomFormLabel>
                      <DatePicker
                        hasError={!!passengerForm.formState.errors.expiryDate}
                        value={asDateOrNull(field.value)}
                        onChange={(d) => field.onChange(d ?? "")}
                        testId={`${TEST_ID_BASE}-expiry-date-input`}
                        dateConstraint="future"
                        timePicker={false}
                      />
                      {passengerForm.formState.errors.expiryDate && (
                        <p style={styles.errorText} className="text-sm mt-1">
                          {passengerForm.formState.errors.expiryDate.message}
                        </p>
                      )}
                    </div>
                  )}
                />
                <div className="label-and-input flex flex-col gap-2">
                  <CustomFormLabel data-testid="labelId">
                    {t("vessel-call.create.table-headers.port-of-embarkation")}
                  </CustomFormLabel>
                  <Autocomplete
                    options={portsOptions}
                    value={(() => {
                      const val = passengerForm.watch("portOfEmbarkation");
                      return val
                        ? {
                            value: val,
                            label:
                              portsOptions.find((o: PortOption) => o.value === val)?.label ?? val,
                          }
                        : null;
                    })()}
                    onChange={(option) => {
                      passengerForm.setValue(
                        "portOfEmbarkation",
                        option?.value ? String(option.value) : "",
                      );
                      passengerForm.trigger("portOfEmbarkation");
                    }}
                    testId={`${TEST_ID_BASE}-portOfEmbarkation-select`}
                    placeholder={
                      isPortsLoading
                        ? t("common.loading")
                        : t("vessel-call.create.table-headers.port-of-embarkation")
                    }
                    maxHeight={200}
                    error={!!passengerForm.formState.errors.portOfEmbarkation}
                    disabled={isPortsLoading}
                  />
                  {passengerForm.formState.errors.portOfEmbarkation && (
                    <p style={styles.errorText} className="text-sm mt-1">
                      {passengerForm.formState.errors.portOfEmbarkation.message}
                    </p>
                  )}
                </div>
                <div className="label-and-input flex flex-col gap-2">
                  <CustomFormLabel data-testid="labelId">
                    {t("vessel-call.create.table-headers.port-of-disembarkation")}
                  </CustomFormLabel>
                  <Autocomplete
                    options={portsOptions}
                    value={(() => {
                      const val = passengerForm.watch("portOfDisembarkation");
                      return val
                        ? {
                            value: val,
                            label:
                              portsOptions.find((o: PortOption) => o.value === val)?.label ?? val,
                          }
                        : null;
                    })()}
                    onChange={(option) => {
                      passengerForm.setValue(
                        "portOfDisembarkation",
                        option?.value ? String(option.value) : "",
                      );
                      passengerForm.trigger("portOfDisembarkation");
                    }}
                    testId={`${TEST_ID_BASE}-portOfDisembarkation-select`}
                    placeholder={
                      isPortsLoading
                        ? t("common.loading")
                        : t("vessel-call.create.table-headers.port-of-disembarkation")
                    }
                    maxHeight={200}
                    error={!!passengerForm.formState.errors.portOfDisembarkation}
                    disabled={isPortsLoading}
                  />
                  {passengerForm.formState.errors.portOfDisembarkation && (
                    <p style={styles.errorText} className="text-sm mt-1">
                      {passengerForm.formState.errors.portOfDisembarkation.message}
                    </p>
                  )}
                </div>
                <div className="label-and-input flex flex-col gap-2">
                  <CustomFormLabel data-testid="labelId">
                    {t("vessel-call.create.table-headers.transit-passenger")}
                  </CustomFormLabel>
                  <div className="flex gap-4 items-center">
                    <Radio
                      size="small"
                      checked={passengerForm.watch("transitPassenger") === true}
                      onChange={() => {
                        passengerForm.setValue("transitPassenger", true);
                        passengerForm.trigger("transitPassenger");
                      }}
                      aria-label="transitPassenger-yes"
                    />
                    <span>{t("common.yes")}</span>
                    <Radio
                      size="small"
                      checked={passengerForm.watch("transitPassenger") === false}
                      onChange={() => {
                        passengerForm.setValue("transitPassenger", false);
                        passengerForm.trigger("transitPassenger");
                      }}
                      aria-label="transitPassenger-no"
                    />
                    <span>{t("common.no")}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {type === "addCrew" ||
        type === "editCrew" ||
        type === "addPortCall" ||
        type === "editPortCall" ||
        type === "addPassenger" ||
        type === "editPassenger" ? (
          <div className="flex gap-3 p-6 pt-4" style={styles.footerBorder}>
            <Button
              fullWidth
              label={<span className="normal-case font-medium">{t(`${LANG_BASE}.cancel`)}</span>}
              variant="text"
              onClick={handleCancelClick}
              testId={`${TEST_ID_BASE}-close-button`}
            />
            <Button
              fullWidth
              label={<span className="normal-case font-medium">{submitButtonLabel}</span>}
              variant="contained"
              onClick={handleSubmit}
              testId={`${TEST_ID_BASE}-submit-button`}
              loading={isSubmitting}
            />
          </div>
        ) : type === "details" || type === "notifications" || type === "statusDetails" ? (
          <div className="flex justify-end gap-3 p-6 pt-4" style={styles.footerBorder}>
            <Button
              label={<span className="normal-case font-medium">{submitButtonLabel}</span>}
              variant="contained"
              onClick={onClose}
              testId={`${TEST_ID_BASE}-ok-button`}
              sx={{ width: "50%" }}
            />
          </div>
        ) : (
          <div className="flex gap-3 p-6 pt-4" style={styles.footerBorder}>
            <Button
              fullWidth
              label={<span className="normal-case font-medium">{t(`${LANG_BASE}.cancel`)}</span>}
              variant="text"
              onClick={onClose}
              testId={`${TEST_ID_BASE}-close-button`}
            />
            <Button
              fullWidth
              label={<span className="normal-case font-medium">{submitButtonLabel}</span>}
              variant="contained"
              onClick={handleSubmit}
              testId={`${TEST_ID_BASE}-submit-button`}
              disabled={isSubmitting || shouldBlockSubmitForPendingUpload}
              loading={isSubmitting}
            />
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default SideDrawer;
